import React, { useState, useReducer } from 'react'
import { sessionStorage } from 'react-storage'
import DefaultLayout, {
  LayoutSpacingOptions,
} from '@src/components/DefaultLayout'
import { homeRoute, genomicsRoute } from '@src/routes'
import { useRouter } from 'next/router'
import {
  GenomicProduct,
  ActiveLinkType,
  GenomicsProperties,
  ReducerTypes,
} from './enum'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '../../messages'
import ManualSearch from './CustomCrispr/Step1/ManualSearch'
import UploadQueries from './CustomCrispr/Step1/UploadQueries'
import { BreadcrumbNavigationUrls } from '@src/routes/Configurators/utils/BreadcrumbNavigationUrlsEnum'
import { useUserSession } from '@utils/useUserSession'
import {
  useCustomCrisprEditLinkQuery,
  useGetCrisprSessionDataQuery,
  SpecificationsForCustomCrisprComponent,
  useGetProductInformationQuery,
} from '@src/queries/ConfiguratorTubesQuery.generated'
import { useCurrentUserQuery } from '@src/queries/CurrentUserQuery.generated'
import {
  useCustomCrisprTotalValidationMutation,
  useCustomCrisprSaveToSessionMutation,
} from '@src/mutations/ConfiguratorTubesMutation.generated'
import Step2 from './CustomCrispr/Step2'
import { initialState, reducer, ContextProps } from './Reducer'
import { default as UUID } from 'uuid'
import ReviewAndConfirm from './CustomCrispr/Step3/ReviewAndConfirm'
import { getProductStatus } from '@src/routes/Configurators/utils/util'
import ConfiguratorPageError from '@src/routes/Configurators/utils/ConfiguratorPageError'
import { ConfiguratorValues } from '@src/routes/Configurators/utils/constants'
import { cloneDeep } from 'lodash'
import ConfiguratorLayout from '@src/routes/Configurators/components/ConfiguratorLayout'
import Button from '@material-ui/core/Button'
import CircleLoader from '@src/components/CircleLoader'
import { ConfiguratorProductInput } from '@src/types/graphql-types'
import DesktopRequired from '../../components/DesktopRequired'
import useZoomAwareBreakpoints from '@src/utils/zoomAwareBreakpoints'
import CommonTabs, { CommonTab } from '@src/components/Common/CommonTabs'
import PerformanceGuaranteeModal from '@src/routes/Configurators/components/PerformanceGuaranteeModal'

interface CustomCrisprEditLinkDetails {
  productType: string
  participantId: string
  countryCode: string
  cartId: string
  cartItemId: string
  store: string
}

export const ConfigContext = React.createContext({} as ContextProps)

const Genomics = () => {
  const router = useRouter()
  const intl = useIntl()
  const { userSession } = useUserSession()
  const { data, loading } = useCurrentUserQuery()
  const [state, dispatch] = useReducer(reducer, initialState)
  const [openError, setOpenError] = React.useState(false)
  const [sessionResponse, setSessionResponse] = React.useState<any>({})
  const countryCd = userSession.country
  const { country, language, product = '' } = router.query
  const [itemDetails, setItemDetails] = React.useState<any>([])
  const [instructions, setInstructions] = React.useState<string>('')
  const [errorMsg, setErrorMsg] = React.useState('')
  const [open, setOpen] = React.useState(false)
  const currentUser: any = data?.me || null
  let loggedIn

  let activeLink = router?.query?.activeLink ?? ''
  const cartId = router?.query?.cartid ?? ''
  const itemId = router?.query?.itemid ?? ''
  const [productCode, setProductCode] = useState<number>(0)
  const [currencyFormat, setCurrencyFormat] = useState<string>('')
  const [decimalSeparator, setDecimalSeparator] = useState<any>('')
  const [prefixCurrecny, setPrefixCurrecny] = useState<any>(true)
  const [thousandSeparator, setThousandSeparator] = useState<any>('')
  const [thousandSpacing, setThousandSpacing] = useState<any>('')
  const [fetchApi, setFetchApi] = React.useState(false)
  const [customCrisprSaveToSession] = useCustomCrisprSaveToSessionMutation()

  const isDesktop = useZoomAwareBreakpoints(960, true)
  const [customCrisprTotalValidationMutation, { loading: nextStepLoading }] =
    useCustomCrisprTotalValidationMutation()
  const date = new Date()
  const timeZone = (date.getTimezoneOffset() / 60) * -1
  if (!loading)
    loggedIn =
      currentUser && currentUser.__typename === GenomicsProperties.LoggedInUser

  let participationId: string
  let locale: string
  let isCSR: boolean
  let isCSRSupervisor: boolean
  let isSAPNextEnabled: boolean
  let isOrderCustomer: boolean
  let isOrderableCustomer: boolean
  let isGuestShoppingCountry: boolean
  let isConfiguratorDisabled: boolean

  let accessToken = ''
  let store = ''
  if (userSession.accessToken) {
    accessToken = userSession.accessToken
  }
  if (userSession.store) {
    store = userSession.store
  }
  if (loggedIn && currentUser) {
    participationId = currentUser.id
    locale = `${userSession.language}_${countryCd}`
    if (locale === '') {
      locale = currentUser?.metadata.locale
    }
    isCSR = currentUser?.roles.isCustomerServiceRep
    isCSRSupervisor = currentUser?.roles.isCustomProductCSR
    isSAPNextEnabled = currentUser.metadata.isSAPNextEnabled
    isGuestShoppingCountry = currentUser.metadata.guestShoppingType
    isOrderCustomer =
      currentUser?.roles.isPublicOrderingCustomer ||
      currentUser?.roles.isQuoteRequester
    if (isGuestShoppingCountry !== null) {
      isOrderableCustomer = true
    } else if (isOrderCustomer && isGuestShoppingCountry === null) {
      isOrderableCustomer = true
    } else {
      isOrderableCustomer = false
    }
    isConfiguratorDisabled =
      currentUser.participantSitePreference?.hideOligoPeptideDesign
  } else if (userSession.accessToken) {
    participationId = userSession.accessToken
    locale = `${userSession.language}_${countryCd}`
    isCSR = false
    isCSRSupervisor = false
    isOrderableCustomer = false
    isSAPNextEnabled = currentUser?.metadata.isSAPNextEnabled
    isConfiguratorDisabled = false
  } else {
    //TODO: commented participationId as a part of fix for STRAT-11633
    //Identify why the page is going in an infinite loop when fetching the id dynamically.
    participationId = UUID.v4()
    locale = `en_${countryCd}`
    isCSR = false
    isCSRSupervisor = false
    isOrderableCustomer = false
    isSAPNextEnabled = true
    isConfiguratorDisabled = false
  }

  const customCrisprEditLinkDetails: CustomCrisprEditLinkDetails = {
    productType: product,
    participantId: participationId,
    countryCode: countryCd,
    cartId: cartId,
    cartItemId: itemId,
    store: store,
  }
  const productDetails: ConfiguratorProductInput = {
    productType: product,
    participantId: participationId,
    countryCode: countryCd,
    isRegUser: loggedIn,
    locale: locale,
    isCSR: isCSR,
    isCSRSupervisor: isCSRSupervisor,
    sapNextEnabled: isSAPNextEnabled,
    timeZone: timeZone.toString(),
    store: store,
  }

  const productInfoInput = {
    productType: product,
    countryCode: countryCd,
    isCsr: isCSR,
    isCsrSupervisor: isCSRSupervisor,
    isOrderableCustomer: isOrderableCustomer,
    regUser: loggedIn,
    locale: locale,
    sapNextEnabled: isSAPNextEnabled,
    hideOligoPeptideDesign: isConfiguratorDisabled,
  }

  const { data: productInformationData } = useGetProductInformationQuery({
    variables: {
      input: productInfoInput,
    },
    fetchPolicy: 'no-cache',
    skip: !accessToken,
    ssr: false,
  })

  if (loggedIn) {
    const referrerParticipantId: string | null = sessionStorage.getItem(
      'CONFIGURATOR_REFERRER_ID'
    )
    if (referrerParticipantId) {
      productDetails.guestParticipantId = referrerParticipantId
    }
  }

  React.useEffect(() => {
    if (productInformationData) {
      const productInformation = productInformationData.getProductInformation
      if (
        loggedIn !== undefined &&
        productInformation &&
        productInformation.displayName
      ) {
        const code = getProductStatus(productInformation.displayName)
        setProductCode(code)
      }
      if (productInformation?.instructions) {
        setInstructions(productInformation.instructions)
      }
      if (productInformation?.currencyFormat) {
        setCurrencyFormat(productInformation.currencyFormat)
      }
      if (productInformation) {
        setDecimalSeparator(productInformation.decimalSeparator)
        setPrefixCurrecny(productInformation.prefixCurrecny)
        setThousandSeparator(productInformation.thousandSeparator)
        setThousandSpacing(productInformation.thousandSpacing)
      }
    }
  }, [productInformationData])

  const { data: crisprEditLinkData } = useCustomCrisprEditLinkQuery({
    variables: {
      product: customCrisprEditLinkDetails,
    },
    fetchPolicy: 'no-cache',
    skip: !accessToken || !(cartId && itemId),
    ssr: false,
  })

  const { data: crisprSessionData } = useGetCrisprSessionDataQuery({
    variables: {
      product: productDetails,
    },
    fetchPolicy: 'no-cache',
    skip: !accessToken,
    ssr: false,
  })

  React.useEffect(() => {
    let sessionResp: any
    if (cartId && itemId && crisprEditLinkData) {
      sessionResp = crisprEditLinkData.customCrisprEditLink?.data
    } else {
      sessionResp = crisprSessionData?.getCrisprSessionData?.data
    }
    if (sessionResp) {
      setFetchApi(true)
      setSessionResponse(sessionResp)
      sessionStorage.removeItem('CONFIGURATOR_REFERRER_ID')
    }
  }, [crisprEditLinkData, crisprSessionData])

  const [disableSpecificationsLink, setDisableSpecificationsLink] =
    React.useState(false)

  const [disableReviewAndConfirmLink, setDisableReviewAndConfirmLink] =
    React.useState(false)

  const url = `/${country}/${language}/${genomicsRoute.index()}?product=${product}&activeLink=${
    ActiveLinkType.SequenceEntry
  }`
  const [fetchAndUpdateSession, setFetchAndUpdateSession] =
    React.useState(false)

  const validateActiveLink = (activeLink) => {
    return activeLink === '' || activeLink == null
      ? ActiveLinkType.SequenceEntry
      : activeLink
  }

  const setInitialState = (sessionData) => {
    const listLength = sessionData[0].listSubModels?.length
    const lastItemId = sessionData[0].listSubModels[listLength - 1].id
    return {
      name: '',
      geneId: '',
      geneSymbol: '',
      sequence: '',
      id: lastItemId + 1,
      isOverridden: false,
      baseCount: 0,
      isCurrentForm: true,
      price: '',
      cartId: '',
      itemId: '',
    }
  }

  //function to rest the active data and save to session.
  //Done as part of MTO-92
  const resetActiveSubModel = () => {
    const { sessionData, currentSubModelIndex } = state
    const lastItemId = sessionData[0].listSubModels[currentSubModelIndex].id
    sessionData[0].listSubModels[currentSubModelIndex] = {
      name: '',
      geneId: '',
      geneSymbol: '',
      sequence: '',
      id: lastItemId,
      isOverridden: false,
      baseCount: 0,
      isCurrentForm: true,
      price: '',
      cartId: '',
      itemId: '',
    }
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...sessionData],
    })
  }

  React.useEffect(() => {
    const { activeLink } = router.query
    const updateDataOnLoad = async () => {
      let enablesequenceList = false
      let isEmptyObj = false
      let mergedSession = false
      if (sessionResponse && fetchApi) {
        if (sessionResponse.length) {
          if (sessionResponse[0].mergedSession) {
            mergedSession = sessionResponse[0].mergedSession
          }
          if (sessionResponse[0].listSubModels?.length) {
            enablesequenceList = true
          }
          delete sessionResponse[0].__typename
          delete sessionResponse[0].mergedSession
          sessionResponse[0].listSubModels.forEach((listSubModel) => {
            delete listSubModel.__typename
            if (listSubModel.name === '' && listSubModel.sequence === '') {
              isEmptyObj = true
            }
            if (listSubModel && listSubModel.listSubModelErrors) {
              listSubModel.listSubModelErrors.forEach((modelError) => {
                if (modelError && modelError.__typename) {
                  delete modelError.__typename
                }
              })
            }
          })
          //set stepNo to 2 for enabling all sidebar options while coming through edit option from cart
          //cartId and physical material will be there in the session.
          //If any changes done in sequence entry page, physicalMaterial will be empty.
          if (
            cartId ||
            (sessionResponse[0].cartId &&
              sessionResponse[0].physicalMaterial !== '')
          )
            state.stepNo = 2
          //set stepNo based on sessionData.pageNo
          else state.stepNo = sessionResponse[0].pageNo

          dispatch({ type: 'SET_STEP_NO', data: state.stepNo })
          if (!isEmptyObj) {
            const emptyStateObj = Object.assign(
              {},
              setInitialState(sessionResponse)
            )
            sessionResponse[0].listSubModels.push(emptyStateObj)
          }
          await dispatch({
            type: 'UPDATE_INPUT',
            data: sessionResponse,
          })

          await dispatch({
            type: 'UPDATE_LIST_SUB_MODEL',
            data: sessionResponse[0].listSubModels,
          })
          await dispatch({
            type: 'UPDATE_CURRENT_INDEX',
            data: sessionResponse[0].listSubModels.length - 1,
          })
          if (enablesequenceList) {
            await dispatch({
              type: 'CRISPR_SEQUENCE_LIST',
              data: true,
            })
          }
          if (mergedSession) {
            dispatch({ type: 'SET_STEP_NO', data: 0 })
            router.push(url)
          }
        } else {
          if (
            activeLink === ActiveLinkType.ConfirmOrder ||
            activeLink === ActiveLinkType.Specifications
          ) {
            dispatch({ type: 'SET_STEP_NO', data: 0 })
            router.push(url)
          }
        }
        setFetchAndUpdateSession(true)
      }
    }
    updateDataOnLoad()
  }, [sessionResponse])

  React.useEffect(() => {
    if (state.stepNo === 1) {
      setDisableSpecificationsLink(false)
      setDisableReviewAndConfirmLink(true)
    } else if (state.stepNo === 2) {
      setDisableSpecificationsLink(false)
      setDisableReviewAndConfirmLink(false)
    } else {
      if (!cartId) {
        setDisableSpecificationsLink(true)
      }
      setDisableReviewAndConfirmLink(true)
    }
    // TODO: need a test before we refactor these dependencies
  }, [state.stepNo, router.query])
  React.useEffect(() => {
    if (
      activeLink === null ||
      activeLink === undefined ||
      activeLink === ActiveLinkType.SequenceEntry
    ) {
      dispatch({ type: ReducerTypes.INIT })
      state.stepNo = 0
    } else if (activeLink === ActiveLinkType.Specifications) {
      state.stepNo = 1
    } else if (activeLink === ActiveLinkType.ConfirmOrder) {
      state.stepNo = 2
    }
    // TODO: need a test before we refactor these dependencies
  }, [])
  React.useEffect(() => {
    if (state.pageError.length === 0) {
      setOpenError(false)
    } else {
      setOpenError(true)
    }
  }, [state.pageError])
  React.useEffect(() => {
    if (state.isDisplayError) {
      setOpen(true)
    } else {
      setOpen(false)
    }
  }, [state.isDisplayError])

  React.useEffect(() => {
    return function cleanupError() {
      !loggedIn
        ? sessionStorage.setItem(
            'CONFIGURATOR_REFERRER_ID',
            productDetails.participantId
          )
        : sessionStorage.getItem('CONFIGURATOR_REFERRER_ID') !== null &&
          sessionStorage.removeItem('CONFIGURATOR_REFERRER_ID')
    }
  }, [])

  if (!router || !router.query) return null
  activeLink = validateActiveLink(activeLink)

  const saveSessionDataToSession = async (input, productInput) => {
    await customCrisprSaveToSession({ variables: { input, productInput } })
  }

  const breadcrumbItems = [
    {
      url: homeRoute.index(),
      label: <FormattedMessage {...messages.CONF_HOME_CUSTOMCRISPR} />,
    },
    {
      url: BreadcrumbNavigationUrls.Custom_Products_customcrispr,
      label: (
        <FormattedMessage {...messages.CONF_CUSTOM_PRODUCT_CUSTOMCRISPR} />
      ),
    },
    {
      url: BreadcrumbNavigationUrls[`Breadcrumb_${product}`],
      label: (
        <FormattedMessage {...messages.CONF_CUSTOM_PRODUCT_NAME_CUSTOMCRISPR} />
      ),
    },
    {
      label: 'Custom CRISPR',
    },
  ]
  const sidebarItems = [
    {
      label: intl.formatMessage(messages.CONF_SEQUENCE_ENTRY),
      activeLink:
        activeLink === ActiveLinkType.SequenceEntry ||
        activeLink === ActiveLinkType.MultipleEntry
          ? true
          : false,
      url:
        activeLink === ActiveLinkType.MultipleEntry
          ? `${genomicsRoute.index()}?product=${product}&activeLink=${
              ActiveLinkType.MultipleEntry
            }`
          : `${genomicsRoute.index()}?product=${product}&activeLink=${
              ActiveLinkType.SequenceEntry
            }`,
      linkDisable: false,
    },
    {
      label: intl.formatMessage(messages.CONF_SPECIFICATIONS),
      activeLink: activeLink === ActiveLinkType.Specifications ? true : false,
      url: `${genomicsRoute.index()}?product=${product}&activeLink=${
        ActiveLinkType.Specifications
      }`,
      linkDisable: disableSpecificationsLink,
    },
    {
      label: intl.formatMessage(messages.CONFIRM_ORDER),
      activeLink: activeLink === ActiveLinkType.ConfirmOrder ? true : false,
      url: `${genomicsRoute.index()}?product=${product}&activeLink=${
        ActiveLinkType.ConfirmOrder
      }`,
      linkDisable: disableReviewAndConfirmLink,
    },
  ]

  const setListFromTotalValidationResp = (validationResponse) => {
    const { sessionData } = state
    if (validationResponse[0].listPhysicalMaterials?.length) {
      sessionData[0].listPhysicalMaterials = validationResponse[0]
        .listPhysicalMaterials as string[]
    }
    if (validationResponse[0].listAliquotings?.length) {
      sessionData[0].listAliquotings = validationResponse[0]
        .listAliquotings as string[]
    }
    if (validationResponse[0].listAmount?.length) {
      sessionData[0].listAmount = validationResponse[0].listAmount as string[]
    }
    if (validationResponse[0].listLentiviralBackbones?.length) {
      sessionData[0].listLentiviralBackbones = validationResponse[0]
        .listLentiviralBackbones as string[]
    }
    if (validationResponse[0].listModification?.length) {
      sessionData[0].listModification = validationResponse[0]
        .listModification as string[]
    }
    if (validationResponse[0].listPurification?.length) {
      sessionData[0].listPurification = validationResponse[0]
        .listPurification as string[]
    }
    if (validationResponse[0].listScale?.length) {
      sessionData[0].listScale = validationResponse[0].listScale as string[]
    }
    if (validationResponse[0].listSpecies?.length) {
      sessionData[0].listSpecies = validationResponse[0].listSpecies as string[]
    }
    if (validationResponse[0].listStructure?.length) {
      sessionData[0].listStructure = validationResponse[0]
        .listStructure as string[]
    }
    if (validationResponse[0].listVectors?.length) {
      sessionData[0].listVectors = validationResponse[0].listVectors as string[]
    }
    if (validationResponse[0].listViralTiters?.length) {
      sessionData[0].listViralTiters = validationResponse[0]
        .listViralTiters as string[]
    }
    if (validationResponse[0].listVolumes?.length) {
      sessionData[0].listVolumes = validationResponse[0].listVolumes as string[]
    }
  }

  const specificationsRoute = `/${country}/${language}/${genomicsRoute.index()}?product=${
    productDetails.productType
  }&activeLink=${ActiveLinkType.Specifications}`

  const handleNextStep = async () => {
    let isEmptyObj = false
    const { sessionData, currentSubModelIndex } = state
    sessionData[0].listSubModels.forEach((listModel) => {
      if (
        sessionData[0].listSubModels.length > 1 &&
        listModel.isCurrentForm &&
        listModel.name === '' &&
        listModel.sequence === ''
      ) {
        isEmptyObj = true
        const index = sessionData[0].listSubModels.indexOf(listModel)
        sessionData[0].listSubModels.splice(index, 1)
      }
    })
    if (isEmptyObj) {
      dispatch({
        type: 'UPDATE_CURRENT_INDEX',
        data: sessionData[0].listSubModels.length - 1,
      })
    }
    const response = await customCrisprTotalValidationMutation({
      variables: { input: sessionData, productInput: productDetails },
    })
    let validationResp
    if (response?.data?.customCrisprTotalValidation)
      validationResp = response?.data?.customCrisprTotalValidation[0]
    if (
      response?.data?.customCrisprTotalValidation &&
      !validationResp.serverError
    ) {
      const validationResponse = cloneDeep(
        response?.data?.customCrisprTotalValidation
      )
      const physicalMaterials = validationResponse[0].listPhysicalMaterials
      const listSubModels = validationResponse[0].listSubModels
      const filteredErrors = listSubModels?.filter(
        (listSubModel) => listSubModel?.listSubModelErrors?.length
      )
      delete validationResponse[0].__typename
      if (validationResponse[0].listSubModels) {
        validationResponse[0].listSubModels.forEach((model) => {
          if (model && model.__typename) {
            delete model?.__typename
          }
          if (model && model.listSubModelErrors) {
            model.listSubModelErrors.forEach((modelError) => {
              window.scrollTo(0, 0)
              if (modelError && modelError.__typename) {
                delete modelError.__typename
              }
            })
          }
        })
      }
      if (validationResponse[0].listError) {
        validationResponse[0].listError.forEach((error) => {
          if (error && error?.__typename) delete error?.__typename
        })
      }
      if (validationResponse[0].listCompanionProducts) {
        delete validationResponse[0].listCompanionProducts
      }
      if (filteredErrors?.length) {
        const isDataUploaded = filteredErrors.filter(
          (errorObj) => !errorObj?.isCurrentForm
        )
        if (!isDataUploaded?.length) {
          const errors: any[] = []
          const validatErrorList = filteredErrors[0]?.listSubModelErrors
          if (validatErrorList) {
            validatErrorList.forEach((error) => {
              if (error && error.key === GenomicsProperties.SequenceKey) {
                errors.push({
                  key: error?.key,
                  value: error?.value,
                })
              }
              if (error && error.key === GenomicsProperties.Name) {
                errors.push({
                  key: error?.key,
                  value: error?.value,
                })
              }
            })
          }
          dispatch({
            type: 'UPDATE_ERRORS',
            data: errors,
          })
        }
      } else {
        if (sessionData[0].listSubModels.length > 100) {
          //dispatch and prop method 'setErrorMsg' set for maximum line item limit
          dispatch({ type: 'IS_DISPLAY_ERROR', data: true })
          setErrorMsg(intl.formatMessage(messages.CONF_UPLOAD_ERROR_MSG))
          window.scrollTo(0, 0)
        } else {
          if (
            !sessionData[0].listPhysicalMaterials?.length &&
            physicalMaterials
          ) {
            physicalMaterials.forEach((material) => {
              if (sessionData[0].listPhysicalMaterials) {
                !!material &&
                  sessionData[0].listPhysicalMaterials.push(material)
              }
            })
          }
          //assign total validation response list items to session data
          setListFromTotalValidationResp(validationResponse)

          if (!isEmptyObj) {
            sessionData[0].listSubModels[currentSubModelIndex].isCurrentForm =
              false
          }
          //updating pageNo = 1 to enable side bar for 'Specifications' Page
          sessionData[0].pageNo = 1
          await saveSessionDataToSession(sessionData, productDetails)

          const listLength = sessionData[0].listSubModels.length
          const lastItemId = sessionData[0].listSubModels[listLength - 1]?.id
          dispatch({
            type: 'UPDATE_CRISPR_CURRENT_INDEX',
            data: lastItemId,
          })
          const emptyStateObj = Object.assign({}, setInitialState(sessionData))
          if (sessionData[0] && sessionData[0].listSubModels) {
            sessionData[0].listSubModels.push(emptyStateObj)
          }
          dispatch({
            type: 'CRISPR_SEQUENCE_LIST',
            data: true,
          })
          //updating pageNo = 1 to enable side bar for 'Specifications' Page
          sessionData[0].pageNo = 1
          if (state.stepNo === 0) {
            dispatch({ type: 'SET_STEP_NO', data: 1 })
            //to clear the previously selected specification page entries if any
            //sessionData[0].physicalMaterial = ''
          }
          dispatch({
            type: 'UPDATE_INPUT',
            data: [...sessionData],
          })
          dispatch({
            type: 'UPDATE_LIST_SUB_MODEL',
            data: [...sessionData[0].listSubModels] as any,
          })
          dispatch({
            type: 'SET_PAGE_ERROR',
            data: '',
          })
          //dispatch and prop method 'setErrorMsg' set for maximum line item limit
          dispatch({ type: 'IS_DISPLAY_ERROR', data: false })
          setErrorMsg('')

          dispatch({
            type: 'UPDATE_ERRORS',
            data: [],
          })
          if (isEmptyObj) {
            dispatch({
              type: 'UPDATE_CURRENT_INDEX',
              data: currentSubModelIndex,
            })
          } else {
            dispatch({
              type: 'UPDATE_CURRENT_INDEX',
              data: currentSubModelIndex + 1,
            })
          }
          router.push(specificationsRoute)
        }
      }
    } else {
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: ConfiguratorValues.InternalServerError,
      })
      window.scrollTo(0, 0)
    }
    dispatch({
      type: 'VALIDATE_ON_CLICK',
      data: true,
    })
  }

  const NextStepButton = () => {
    if (
      product === GenomicProduct.CustomCrispr &&
      activeLink === ActiveLinkType.SequenceEntry
    ) {
      return (
        <Button
          id="customCrispr-nextStep-button"
          variant="contained"
          size="medium"
          color="primary"
          onClick={handleNextStep}
        >
          <FormattedMessage {...messages.CONFIG_NEXT_STEP} />
        </Button>
      )
    }
    return <></>
  }

  const errorItems = [
    {
      hasError: openError,
      setOpen: setOpenError,
      errorMessage: state.pageError,
    },
    {
      hasError: !!(errorMsg && state.isDisplayError && open),
      setOpen: setOpen,
      errorMessage: errorMsg,
    },
  ]
  const onTabChange = (tab: string) => {
    resetActiveSubModel()
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          activeLink: tab,
        },
      },
      undefined,
      { shallow: true }
    )
  }
  const tabs: CommonTab[] = [
    {
      label: intl.formatMessage({
        id: 'SINGLE_PRODUCT_ENTRY',
        defaultMessage: 'Single Product Entry',
      }),
      component: (
        <ManualSearch
          productDetails={productDetails}
          instructions={instructions}
          setErrorMsg={setErrorMsg}
          errorMessages={errorItems}
        />
      ),
      id: 'single-product-entry',
      selectCallback: () => onTabChange(ActiveLinkType.SequenceEntry),
    },
    {
      label: intl.formatMessage({
        id: 'MULIPLE_PRODUCT_ENTRY',
        defaultMessage: 'Multiple Product Entry',
      }),
      component: <UploadQueries productDetails={productDetails} />,
      id: 'multiple-product-entry',
      selectCallback: () => onTabChange(ActiveLinkType.MultipleEntry),
    },
  ]
  const activeTabIndex =
    router.query?.activeLink === ActiveLinkType.MultipleEntry
      ? 1
      : router.query?.activeLink === ActiveLinkType.MultipleEntry
        ? 0
        : undefined

  const GenomicsContentComponent = (getSpecificationsForCustomCrispr) => {
    if (product === GenomicProduct.CustomCrispr) {
      if (
        activeLink === ActiveLinkType.SequenceEntry ||
        activeLink === ActiveLinkType.MultipleEntry
      ) {
        return (
          <CommonTabs
            tabs={tabs}
            hideDivider
            additionalLink={<PerformanceGuaranteeModal />}
            activeTabIndex={activeTabIndex}
          />
        )
      } else if (activeLink === ActiveLinkType.Specifications) {
        return (
          <Step2
            specificationsData={getSpecificationsForCustomCrispr}
            productDetails={productDetails}
            setItemDetails={setItemDetails}
          />
        )
      }
      if (activeLink === ActiveLinkType.ConfirmOrder) {
        return (
          <div>
            <ReviewAndConfirm
              productDetails={productDetails}
              itemDetails={itemDetails}
              instructions={instructions}
              currencyFormat={currencyFormat}
              thousandSeparator={thousandSeparator}
              thousandSpacing={thousandSpacing}
              prefixCurrecny={prefixCurrecny}
              decimalSeparator={decimalSeparator}
            />
          </div>
        )
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  return (
    <ConfigContext.Provider value={{ state, dispatch }}>
      <SpecificationsForCustomCrisprComponent
        ssr={false}
        variables={{
          product: productDetails,
        }}
      >
        {({ loading, error, data }) => {
          if (loading) return null
          if (
            error ||
            !data ||
            !data.getSpecificationsForCustomCrispr ||
            !fetchAndUpdateSession
          )
            return null
          const { getSpecificationsForCustomCrispr } = data
          return (
            <DefaultLayout spacing={LayoutSpacingOptions.SM}>
              {productCode !== 0 ? (
                <ConfiguratorPageError
                  productCode={productCode}
                  country={countryCd}
                />
              ) : isDesktop ? (
                <ConfiguratorLayout
                  title={<FormattedMessage {...messages.CUSTOM_CRISPR} />}
                  sidebarItems={sidebarItems}
                  breadcrumbItems={breadcrumbItems}
                  bodyContent={GenomicsContentComponent(
                    getSpecificationsForCustomCrispr
                  )}
                  nextStepButton={<NextStepButton />}
                  disableRootStyle={
                    activeLink === ActiveLinkType.Specifications
                  }
                />
              ) : (
                <DesktopRequired />
              )}
              {nextStepLoading && <CircleLoader />}
            </DefaultLayout>
          )
        }}
      </SpecificationsForCustomCrisprComponent>
    </ConfigContext.Provider>
  )
}
export default Genomics
